import { ApiResponse, ApiService } from './api.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import { SeriesSubscription } from '@models/series-subscription';
import { SyncStatusService } from './sync-status.service';

@Injectable({
  providedIn: 'root',
})
export class SeriesSubscriptionDataService
  extends SyncStatusService
  implements OnDestroy
{
  public seriesSubscriptions: Observable<SeriesSubscription[]>;
  private _seriesSubscription: BehaviorSubject<SeriesSubscription[]>;
  private _loaded = false;
  private data: {
    seriesSubscriptions: SeriesSubscription[];
  };
  private _destroy = new Subject<void>();
  private URL = 'series-subscriptions';

  constructor(private api: ApiService) {
    super();
    this.data = {
      seriesSubscriptions: [],
    };
    this._seriesSubscription = new BehaviorSubject([]);
    this.seriesSubscriptions = this._seriesSubscription.asObservable();
    this._publish();
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._clearSyncTimeout();
  }

  seriesSubscriptionExist(): boolean {
    return this.data.seriesSubscriptions.length > 0;
  }

  byId(id: string): SeriesSubscription {
    return this.data.seriesSubscriptions.find((p) => p.id === id);
  }

  /**
   * Cached loader for SeriesSubscription
   *
   * @returns {Promise<Array<SeriesSubscription>>}
   * @memberof SeriesSubscriptionDataService
   */
  async load(force = false): Promise<Array<SeriesSubscription>> {
    if (this._loaded && !force) {
      return Promise.resolve([...this.data.seriesSubscriptions]);
    }
    const dbLoadStart: number = new Date().getTime();
    try {
      const docs: ApiResponse = await this.api.allDocs(this.URL);
      this._lastSync = docs.until;
      this.data.seriesSubscriptions = docs.results.map((payload) =>
        this._payloadToInstance(payload),
      );
      this._loaded = true;
      this._publish();

      const delta = new Date().getTime() - dbLoadStart;
      const numSeriesSubscription =
        (this.data.seriesSubscriptions &&
          this.data.seriesSubscriptions.length) ||
        0;
      console.debug('Database', delta, 'Load', 'SeriesSubscription');
      console.debug('Num SeriesSubscription', numSeriesSubscription);
    } catch (e) {
      console.error(e);
      throw e;
    }
    return this.data.seriesSubscriptions;
  }

  public loadById(id: string): Promise<SeriesSubscription> {
    return this.api
      .get<SeriesSubscription>(this.URL, id)
      .then((p) => this._payloadToInstance(p));
  }

  public getById(id: string): SeriesSubscription {
    return this.data.seriesSubscriptions.find((obj) => obj.id === id);
  }

  public delete(id: string): Promise<void> {
    return this.api
      .delete<SeriesSubscription>(this.URL, id)
      .then(() => this._broadcastDelete(id));
  }

  /**
   * Get active subscription for Series if there is one.
   * The API only sends active subscriptions.
   * Historical (expired) subscriptions are not sent.
   *
   * @param {string} seriesId
   * @returns {SeriesSubscription}
   * @memberof SeriesSubscriptionDataService
   */
  public getBySeries(seriesId: string): SeriesSubscription {
    return this.data.seriesSubscriptions.find(
      (subscription) => subscription.series === seriesId,
    );
  }

  public unload(): void {
    'Logged out then clear seriesSubscriptions';
    this.data.seriesSubscriptions = [];
    this._publish();
  }

  public async createOrUpdate(
    seriesSubscription: SeriesSubscription,
  ): Promise<SeriesSubscription> {
    let result, updated: SeriesSubscription;
    try {
      result = await this.api.put<SeriesSubscription>(
        this.URL,
        seriesSubscription.id,
        seriesSubscription,
        seriesSubscription.updated,
      );
      updated = this._addUpdate(result);
      this._publish();
    } catch (e) {
      if (e.status !== 409) {
        throw e;
      }
      // 409 is a conflict, meaning the doc was updated on the server
      // reload it and rethrow exception so user can be informed.
      const obj = await this.api.get<SeriesSubscription>(
        this.URL,
        seriesSubscription.id,
      );
      updated = this._addUpdate(obj);
      this._publish();
      throw e;
    }
    return updated;
  }

  protected async _getLatestFromApi(): Promise<SeriesSubscription[]> {
    const docs: ApiResponse = await this.api.allDocs(this.URL, this._lastSync);
    this._lastSync = docs.until;
    return docs.results;
  }

  protected _addUpdate(payload: SeriesSubscription) {
    const seriesSubscription: SeriesSubscription =
      this._payloadToInstance(payload);
    this._processChange(seriesSubscription);
    return seriesSubscription;
  }

  protected _publish(): void {
    // new object list to trigger change detection and isolate changes.
    if (!this._seriesSubscription.closed) {
      this._seriesSubscription.next([...this.data.seriesSubscriptions]);
    }
  }

  private _payloadToInstance(
    seriesSubscription: SeriesSubscription,
  ): SeriesSubscription {
    const instance = new SeriesSubscription({ ...seriesSubscription });
    return instance;
  }

  private _processChange(
    seriesSubscription: SeriesSubscription,
  ): SeriesSubscription {
    // there is only one entry per series from the API so
    // use that instead of the possibly changing subscription id.
    this.data.seriesSubscriptions = [
      ...this.data.seriesSubscriptions.filter(
        (p) => p.series !== seriesSubscription.series,
      ),
      seriesSubscription,
    ];
    return seriesSubscription;
  }

  private _broadcastDelete(id: string): void {
    this.data.seriesSubscriptions = this.data.seriesSubscriptions.filter(
      (p) => p.id !== id,
    );
    this._publish();
  }
}
